import React, { useMemo } from 'react'
import { Area, Line, ComposedChart as RCComposedChart, ResponsiveContainer, ReferenceLine, ReferenceArea } from 'recharts'
import { CartesianGrid, XAxis, YAxisLeft, YAxisRight } from '../../shared/utils/rechartComps'
import { traceConf } from '../../../system-charts/shared/config/traceConf'
import { getUniqueIdentifierFromCustomTrace } from '../../../system-charts/shared/utils/getUniqueIdentifierFromCustomTrace'
import { getChartColor } from 'src/Libs/system-charts/shared/config/chartColors'
import { useTheme } from '@mui/material/styles'
import { CUSTOM_UNITS, CustomTrace, RechartData } from '../../shared/utils/chartModel'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { applySignsToData } from 'src/Libs/system-data-handler/utils/chartFunctions'
import { Box, useMediaQuery } from '@mui/material'
import { SharedChartProps } from '../../shared/types'
import { REFERENCE_AREA_STROKE_OPACITY } from '../../shared/utils/consts'
import { isUndefined } from 'lodash'
import getIsTouchDevice from 'src/Libs/utils/get-is-touch-device'
import { WeatherBreakthroughDot } from '.'

export interface LineProps {
  key?: string
  dataKey: string
  yAxisId: string
  stroke: string
  strokeWidth: number
  strokeDasharray: string | undefined
  opacity: number
  hide: boolean | undefined
  dot: boolean | JSX.Element
  activeDot: boolean
  isAnimationActive: boolean
  connectNulls: boolean
}

const LineChart: React.FC<SharedChartProps> = ({
  unit,
  minPrefix,
  secondUnit,
  xAxisTicks,
  xAxisFormat,
  xAxisInterval,
  rechartData,
  customTraces,
  showForecast,
  impossibleTraces,
  hiddenTraces,
  dstSwitchTs,
  height,
  view,
  chartEventHandlers,
  children,
  zoomStartIndex,
  zoomEndIndex,
  handleDoubleClick,
  isWeatherBreakthroughDotVisible,
  calculatedMinPrefix,
}) => {
  const { t } = useCustomTranslation()
  const changedRechartData: RechartData = applySignsToData(rechartData, customTraces)
  const theme = useTheme()
  const downSm = useMediaQuery(theme.breakpoints.down('sm'))
  const downMd = useMediaQuery(theme.breakpoints.down('md'))
  const upMd = useMediaQuery(theme.breakpoints.up('md'))
  const isTouchDevice = getIsTouchDevice()

  const linesAndAreas = useMemo(() => {
    const customTraceAreaOrStackID = (customTrace: CustomTrace, key: string | null, lineProps: LineProps, forecastUnit?: string): JSX.Element => {
      if (customTrace.type === 'area' || customTrace.stackId) {
        return (
          <Area
            key={key}
            fill={customTrace.color ?? getChartColor(customTrace.key)}
            unit={traceConf[customTrace.key].unit ?? unit}
            stackId={customTrace.stackId}
            {...lineProps}
          />
        )
      } else {
        return <Line key={key} type="monotone" unit={forecastUnit ?? traceConf[customTrace.key].unit ?? unit} {...lineProps} />
      }
    }

    const createLinesAndAreas = (customTrace: CustomTrace): React.ReactElement[] => {
      if (traceConf[customTrace.key]) {
        const yAxisId = CUSTOM_UNITS.includes(traceConf[customTrace.key].unit ?? '') ? 'right' : 'left'
        const key = getUniqueIdentifierFromCustomTrace(customTrace)
        const dot = customTrace.key === 'ForecastPower' && isWeatherBreakthroughDotVisible ? <WeatherBreakthroughDot /> : false

        const lineProps = {
          dataKey: customTrace.alternateKey ?? customTrace.key,
          yAxisId: yAxisId,
          stroke: customTrace.color ?? getChartColor(customTrace.key),
          // name="OOF" // legend and hover
          strokeWidth: customTrace.thickness || traceConf[customTrace.key].custom.thickness || 1,
          strokeDasharray: customTrace.dash === 'dash' ? '5 5' : customTrace.dash === 'dot' ? '2 2' : undefined,
          opacity: customTrace.opacity || traceConf[customTrace.key].custom.opacity || 1,
          hide:
            impossibleTraces.includes(customTrace.alternateKey ?? customTrace.key) ||
            hiddenTraces.includes(customTrace.alternateKey ?? customTrace.key) ||
            customTrace.hidden,
          dot,
          activeDot: !isTouchDevice,
          isAnimationActive: false,
          connectNulls: true,
        }

        const components = [customTraceAreaOrStackID(customTrace, key, lineProps)]

        if (traceConf[customTrace.key].hasForecast) {
          const lineProps2 = {
            ...lineProps,
            dataKey: 'f-' + lineProps.dataKey,
            strokeDasharray: '5 5',
            opacity: lineProps.opacity * 0.5,
            hide: impossibleTraces.includes(customTrace.key) || hiddenTraces.includes(customTrace.key) || !showForecast || customTrace.hidden,
          }
          const key2 = 'f-' + key

          components.push(customTraceAreaOrStackID(customTrace, key2, lineProps2, unit))
        }
        return components
      }
      return []
    }

    if (!customTraces) return []
    return customTraces.flatMap(customTrace => createLinesAndAreas(customTrace))
  }, [customTraces, isWeatherBreakthroughDotVisible, impossibleTraces, hiddenTraces, isTouchDevice, showForecast, unit])

  const hideASOC = hiddenTraces.includes('ASOC')

  const _CartesianGrid = useMemo(() => CartesianGrid({ isSmall: downSm }), [downSm])
  const _XAxis = useMemo(
    () =>
      XAxis({
        tickFormat: xAxisFormat,
        xAxisInterval,
        tick: { fill: theme.palette.text.primary },
        ticks: xAxisTicks,
        mirror: false,
        isSmall: downSm,
      }),
    [xAxisFormat, xAxisInterval, theme, xAxisTicks, downSm]
  )
  const _YAxisLeft = useMemo(
    () =>
      YAxisLeft({
        view: view,
        minPrefix: minPrefix,
        exactPrefix: calculatedMinPrefix,
        tick: { fill: theme.palette.text.primary },
        mirror: true,
        isSmall: downSm,
      }),
    [view, theme, calculatedMinPrefix, downSm, minPrefix]
  )
  const _YAxisRight = useMemo(
    () =>
      YAxisRight({
        isHidden: hideASOC,
        tick: { fill: theme.palette.text.primary },
        unit: secondUnit,
        mirror: downSm,
        isSmall: downSm,
      }),
    [theme, secondUnit, downSm, hideASOC]
  )

  return (
    <Box sx={{ userSelect: 'none', width: '100%' }} onDoubleClick={handleDoubleClick}>
      <ResponsiveContainer height={height} minHeight={downMd ? 350 : undefined} width={'100%'}>
        <RCComposedChart
          data={changedRechartData.data}
          style={
            upMd
              ? undefined
              : {
                  userSelect: 'none',
                }
          }
          {...chartEventHandlers}
        >
          {(upMd && !isUndefined(zoomStartIndex) && !isUndefined(zoomEndIndex)) || !upMd ? (
            <ReferenceArea yAxisId={'right'} x1={zoomStartIndex || 0} x2={zoomEndIndex || 0} strokeOpacity={REFERENCE_AREA_STROKE_OPACITY} />
          ) : null}
          {_CartesianGrid}
          {_XAxis}
          {dstSwitchTs && (
            <>
              <defs>
                <pattern id="dashedPattern" patternUnits="userSpaceOnUse" width="6" height="6" patternTransform="rotate(45)">
                  <rect x="0" y="0" width="3" height="6" fill={theme.palette.error.light} />
                  <rect x="3" y="0" width="3" height="6" fill={theme.palette.grey.shadowLight} />
                </pattern>
              </defs>
              <Area
                isAnimationActive={false}
                type="monotone"
                dataKey="winterToSummerTimeSwitchArea"
                yAxisId="right"
                fill="url(#dashedPattern)"
                stroke="transparent"
              />
              <ReferenceLine
                x={dstSwitchTs}
                xAxisId={0}
                yAxisId={'left'}
                label={t('systemDataHandler.dstSwitch')}
                stroke="red"
                strokeDasharray={'3 3'}
                alwaysShow={true}
              />
            </>
          )}
          {linesAndAreas}
          {_YAxisLeft}
          {_YAxisRight}
          {children}
        </RCComposedChart>
      </ResponsiveContainer>
    </Box>
  )
}

export default LineChart
